import Box from 'UIKit/Box/Box'
import Grid from 'UIKit/Box/Grid'
import { BoxProps } from 'UIKit/Box/types'
import Button from 'UIKit/Button'
import Text from 'UIKit/Text'
import { ProviderInfo } from 'config/types'
import { RowCenter } from 'layout/Components/Row'
import React, { useState } from 'react'
import { Trans } from 'react-i18next'
import { useAppSelector } from 'state'
import styled, { css } from 'styled-components'
import ProviderBannerItem from 'views/Home/components/ProviderBannerItem'

export interface ProviderFilterContainerProps extends BoxProps {
  selectedProviders: string[]
  onProvidersChanged: (codes: string[]) => void
  submitCallback?: () => void
}
const MAX_PROVIDER = 20

const ProviderFilterContainer: React.FC<ProviderFilterContainerProps> = ({
  onProvidersChanged,
  selectedProviders,
  submitCallback,
  ...props
}) => {
  const providers = useAppSelector((state) => state.app.providers)
  const [selected, setSelected] = useState<string[]>(selectedProviders)

  const isEmptyState = selected.length === 0

  const handleSelected = (code: string) => {
    let _selected = [...selected]
    if (_selected.includes(code)) {
      _selected = _selected.filter((select) => select !== code)
    } else {
      _selected.push(code)
    }
    setSelected(_selected)
  }

  const handleSubmit = () => {
    const isDfferent =
      selected.length !== selectedProviders.length ||
      selected.filter((select) => !selectedProviders.includes(select)).length > 0

    if (isDfferent) {
      onProvidersChanged(selected)
    }

    if (submitCallback) {
      submitCallback()
    }
  }

  const handleClear = () => {
    setSelected([])
  }

  return (
    <Container width="220px" p={[, , '10px 10px 12px 10px']} className="provider-filter-container" {...props}>
      <Grid
        gridTemplateColumns={['1fr 1fr 1fr', , '1fr 1fr']}
        gridGap="8px"
        maxHeight="256px"
        width="100%"
        overflow="auto"
        px="2px"
        py="2px"
      >
        {providers.length > 0 &&
          providers.map((provider: ProviderInfo) => (
            <StyledProvider
              $active={selected.includes(provider.code)}
              onClick={() => handleSelected(provider.code)}
              provider={provider}
              key={`item-provider-${provider.code}`}
            />
          ))}
      </Grid>

      <RowCenter flexDirection="column" mt="16px" px="2px">
        <Button
          width="100%"
          mb="8px"
          disabled={selected.length > MAX_PROVIDER}
          onClick={handleSubmit}
          height={[, , 'fit-content']}
          style={{
            borderRadius: '4px',
          }}
        >
          {selected.length > MAX_PROVIDER ? (
            <Text fontSize={['14px', , '12px']} fontWeight={500} py="4px">
              <Trans>Please select less than 20 providers</Trans>
            </Text>
          ) : (
            <Text fontSize={['14px', , '12px']} fontWeight={700} height="24px" lineHeight="24px">
              <Trans>Apply</Trans>
            </Text>
          )}
        </Button>

        <Button
          onClick={handleClear}
          width="100%"
          style={{
            background: 'transparent',
          }}
          disabled={isEmptyState}
          height={['32px', , '24px']}
        >
          <Text fontSize={['14px', , '12px']} fontWeight={700} color={isEmptyState ? 'textSubtle' : 'text'}>
            <Trans>Clear</Trans>
          </Text>
        </Button>
      </RowCenter>
    </Container>
  )
}

const Container = styled(Box)`
  border-radius: 22px;
  background: ${({ theme }) => theme.colors.bg_surface};
  border: 1px solid ${({ theme }) => theme.colors.buttonBackgroundAlt2};
`

const StyledProvider = styled(ProviderBannerItem)<{ $active: boolean }>`
  cursor: pointer;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.radii.button};
  position: relative;
  box-sizing: border-box;

  .container-provider-item {
    background: rgba(35, 44, 61, 0.6) !important;
  }

  ${({ $active }) =>
    $active &&
    css`
      border: 1px solid ${({ theme }) => theme.colors.primary};
      .container-provider-item {
        background: transparent !important;
      }
    `}

  &:hover {
    background: rgba(153, 153, 153, 0.16);
  }
`

export default ProviderFilterContainer
